/* eslint-disable */
import React, {
  useState,
  type FC,
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  type ChangeEvent,
} from 'react';
import { type IChatProps } from 'types/pages';
import {
  TextField,
  Box,
  Autocomplete,
  Chip,
  Checkbox,
  FormControlLabel,
  ListItemText,
  List,
  Radio,
  RadioGroup,
  useTheme,
  // { type SelectChangeEvent }
} from '@mui/material';
import { debounce } from 'lodash';
import Grid from '@mui/material/Grid';
import Buttons from 'components/elements/Buttons';
import { strings } from 'i18n/Strings';
import { useDispatch, useSelector } from 'react-redux';
import { setSnackBarFailed, setSnackBarSuccess } from 'store/snackBar';
import { type RootState } from 'store/rootReducer';
import {
  createGroupChatConversation,
  getEmailListService,
  getAllOrgCompanyNameService,
} from 'service/ChatService';
import { addChatConversationType } from 'types/services';
import { groupChatContants } from 'constants/constantText';
import { colorTheme } from 'theme';

const GroupChat: FC<any> = ({
  onClose,
  getConversationDetails,
}): JSX.Element => {
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  const [email, setEmail] = useState('');
  const [emailList, setEmailList] = useState([]);
  const [orgEmployeeEmail, setOrgEmployeeEmail] = useState([]);
  const [selectedEmailList, setSelectedEmailList] = useState([]);
  const [selectedOrgEmailList, setSelectedOrgEmailList] = useState([]);
  const [selectedRadio, setSelectedRadio] = React.useState('All User');
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [description, setDescription] = useState('');
  // const [imageFile, setImageFile] = useState('');
  const [formError, setFormError] = useState<IChatProps>({
    email: '',
    description: '',
  });
  const [inputValue, setInputValue] = useState('');
  const radioOptions = [
    groupChatContants.allUser,
    groupChatContants.selectEmployers,
    groupChatContants.selectEmail,
  ];

  const dispatch = useDispatch();
  const { empId, firstName, lastName } = useSelector(
    (state: RootState) => state.onboarding,
  );
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    selectedRadio === groupChatContants.selectEmployers && getAllOrgEmailList();
    if (inputRef.current != null) {
      inputRef.current.focus();
    }
  }, [selectedRadio]);

  const handleInputChange = (
    type: string,
    e: { target: { value: string } },
  ): undefined | string => {
    switch (type) {
      case 'description': {
        setDescription(e.target.value);
        return;
      }
      default:
        return '';
    }
  };

  const handleValidation = (
    type: string,
    e: { target: { value: string } },
  ): void => {
    const err: IChatProps = {};
    switch (type) {
      case 'description': {
        if (e.target.value === '') {
          err.description = strings.displayText.lastNameRequiredText;
        }
        setFormError({ ...formError, description: err.description });
        break;
      }
      default:
        break;
    }
  };

  const debounceHandler = useCallback(
    debounce((type: string, e: { target: { value: string } }) => {
      handleValidation(type, e);
    }, 600),
    [],
  );

  const getEmailList = useCallback(async (email: string) => {
    await getEmailListService(email)
      .then((res) => {
        if (typeof res !== 'undefined' && 'emailInfo' in res) {
          const { emailInfo } = res;
          if (emailInfo) {
            const mailList = [...new Set([...emailInfo, ...emailList])];
            setEmailList(mailList);
            // setConversation([basicInfo]);
          } else {
            console.log('basicInfo is undefined or null');
          }
        }
      })
      .catch((err) => {
        console.log(err);
        dispatch(setSnackBarFailed({ snackBarMessage: err.response.data.msg }));
      });
  }, []);

  const handleResponse = async (promise, successCallback) => {
    try {
      const res = await promise();
      if (res && 'companyInfo' in res) {
        successCallback(res.companyInfo);
      }
    } catch (err) {
      console.error(err);
      dispatch(
        setSnackBarFailed({
          snackBarMessage: err.response?.data?.msg || 'An error occurred',
        }),
      );
    }
  };

  const getAllOrgEmailList = () => {
    handleResponse(getAllOrgCompanyNameService, setOrgEmployeeEmail);
  };

  const debounceGetEmailList = useMemo(() => {
    return debounce(getEmailList, 1000);
  }, [getEmailList]);

  const handleEmailChange = async (
    e: ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const { value } = e.target;

    setEmail(value);
    if ((value.length > 2 || value === 'ie') && email.length > 0) {
      debounceGetEmailList(value);
    }
  };

  const handleCheckboxChange = (option: any) => {
    // Check for no employees in company
    if (Number(option?.employeeCount) === 0) {
      dispatch(
        setSnackBarFailed({
          snackBarMessage: `Employer ${option?.company_name} has no employees!`,
        }),
      );
      return;
    }
    const newValue = selectedOrgEmailList.some((item) => item.id === option.id)
      ? selectedOrgEmailList.filter((item) => item.id !== option.id)
      : [
          ...selectedOrgEmailList,
          {
            id: option.id,
            company_name: option.company_name,
          },
        ];
    setSelectedOrgEmailList(newValue);
    handleDropdownClose();
  };

  const validateForm = (): boolean => {
    const err: any = {};

    if (
      selectedRadio === groupChatContants.selectEmail &&
      selectedEmailList.length === 0
    )
      err.email = strings.displayText.emailEmptyText;

    if (
      selectedRadio === groupChatContants.selectEmployers &&
      selectedOrgEmailList.length === 0
    )
      err.email = strings.displayText.employeerNameEmptyText;

    if (description === '')
      err.description = strings.displayText.desccriptionRequiredText;

    setFormError({ ...err });
    return Object.keys(err).length < 1;
  };

  const handleSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);

    let orgEmployeeCompanyList: number[] = [];

    if (selectedOrgEmailList && Array.isArray(selectedOrgEmailList)) {
      const orgCompanyIds = selectedOrgEmailList.map((org) => org.id);
      orgEmployeeCompanyList = [...orgEmployeeCompanyList, ...orgCompanyIds];
    }

    const formDescription = data.get('description')?.toString() ?? '';
    const userListMappings = {
      'All User': 'All User',
      'Select Employers': orgEmployeeCompanyList,
      'Select Email': selectedEmailList,
    };

    const value: addChatConversationType = {
      userList: userListMappings[selectedRadio] || [],
      chatType: selectedRadio,
      conversationDetails: {
        // userId,
        mmEmployeeId: empId, // senderId
        userName: firstName ? `${firstName} ${lastName}` : 'mm Admin',
        messages: formDescription !== '' ? formDescription : 'image file',
        senderType: 'employee',
        createdBy: empId,
        conversationType: 'conversation-group-message', // 'initial-conversation-created-by-employee'
      },
      messageType: 'group-message',
    };
    const isValid = validateForm();

    if (isValid) {
      void (async () => {
        // create bulk chat Conversation service here
        createGroupChatConversation(value)
          .then((res) => {
            dispatch(
              setSnackBarSuccess({
                snackBarMessage: strings.displayText.chatCreatedText,
              }),
            );

            getConversationDetails();
            onClose();
          })
          .catch((err) => {
            dispatch(
              setSnackBarFailed({ snackBarMessage: err.response.data.msg }),
            );
            console.error(`Something went wrong: ${String(err)}`);
          });
      })();
    }
  };

  const handleDropdownOpen = () => {
    setDropdownOpen(true);
  };

  const handleDropdownClose = () => {
    setDropdownOpen(false);
  };

  function CustomRadioButtonGroup({ label, value, onChange, options }) {
    return (
      <RadioGroup value={value} onChange={(e) => onChange(e.target.value)}>
        <Box sx={customStyles.radioBoxStyle}>
          {options.map((option) => (
            <FormControlLabel
              key={option}
              value={option}
              control={<Radio color="success" />}
              label={option}
            />
          ))}
        </Box>
      </RadioGroup>
    );
  }

  const handleRadioChange = (newValue) => {
    setSelectedRadio(newValue);
    if (groupChatContants.selectEmployers !== newValue) {
      setSelectedOrgEmailList([]);
    }
  };

  const customStyles = {
    boxContainer: {
      maxWidth: '40rem',
      minWidth: '20rem',
    },
    textErrorStyle: {
      '& p': {
        color: colors.primaryRed[100],
      },
    },
    labelStyle: { display: 'flex', alignItems: 'center' },
    marginLeftStyle: {
      marginLeft: '8px',
    },
    radioBoxStyle: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '16px',
    },
  };

  return (
    <Fragment>
      <Box sx={customStyles.boxContainer}>
        <Box
          component="form"
          noValidate
          autoComplete="off"
          onSubmit={handleSubmit}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <CustomRadioButtonGroup
                label="Options"
                value={selectedRadio}
                onChange={handleRadioChange}
                options={radioOptions}
              />
            </Grid>

            {selectedRadio === groupChatContants.selectEmployers && (
              <Grid item xs={12} margin={'auto'}>
                <Autocomplete
                  sx={customStyles.textErrorStyle}
                  multiple
                  id="combo-box-demo"
                  options={orgEmployeeEmail?.map((option) => ({
                    id: option.id,
                    company_name: option.company_name,
                  }))}
                  value={selectedOrgEmailList}
                  onChange={(_, newValue) => {
                    setSelectedOrgEmailList(newValue);
                    handleDropdownClose();
                  }}
                  open={isDropdownOpen}
                  onOpen={handleDropdownOpen}
                  onClose={handleDropdownClose}
                  getOptionLabel={(option) => option.company_name}
                  filterOptions={(options, state) => {
                    setInputValue(state.inputValue); // Update the local state with the input value
                    return options.filter((option) =>
                      option.company_name
                        ?.toLowerCase()
                        ?.includes(state.inputValue.toLowerCase()),
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={strings.displayText.selectEmployers}
                      variant="outlined"
                      row={2}
                      helperText={formError?.email}
                    />
                  )}
                  renderTags={(value, getTagProps) => (
                    <>
                      {value?.map((option, index) => (
                        <Chip
                          key={option.id} // Use 'id' as the key
                          variant="outlined"
                          label={option.company_name}
                          {...getTagProps({ index })}
                        />
                      ))}
                    </>
                  )}
                  ListboxComponent={(props) => (
                    <List {...props}>
                      <li>
                        <label style={customStyles.labelStyle}>
                          <Checkbox
                            color="primary"
                            checked={
                              selectedOrgEmailList.length ===
                              orgEmployeeEmail.length
                            }
                            onChange={() => {
                              if (
                                selectedOrgEmailList.length ===
                                orgEmployeeEmail.length
                              ) {
                                // Deselect all employees
                                setSelectedOrgEmailList([]);
                              } else {
                                // Select all employees
                                const allEmails = orgEmployeeEmail?.map(
                                  (option) => ({
                                    id: option.id,
                                    company_name: option.company_name,
                                  }),
                                );
                                setSelectedOrgEmailList(allEmails);
                              }
                              handleDropdownClose();
                            }}
                          />
                          <div style={customStyles.marginLeftStyle}>
                            <ListItemText
                              primary={strings.displayText.allEmployers}
                            />
                          </div>
                        </label>
                      </li>

                      {orgEmployeeEmail?.map(
                        (option) =>
                          option.company_name
                            .toLowerCase()
                            .includes(inputValue.toLowerCase()) && (
                            <li key={option.id}>
                              {' '}
                              {/* Use 'id' as the key */}
                              <label>
                                <Grid container alignItems="center" spacing={1}>
                                  <Grid item>
                                    <Checkbox
                                      color="primary"
                                      checked={selectedOrgEmailList.some(
                                        (item) => item.id === option.id,
                                      )}
                                      onChange={() =>
                                        handleCheckboxChange(option)
                                      }
                                    />
                                  </Grid>
                                  <Grid item>
                                    <ListItemText
                                      primary={option.company_name}
                                    />
                                  </Grid>
                                </Grid>
                              </label>
                            </li>
                          ),
                      )}
                    </List>
                  )}
                />
              </Grid>
            )}

            {selectedRadio === groupChatContants.selectEmail && (
              <Grid item xs={12} margin={'auto'}>
                <Autocomplete
                  sx={customStyles.textErrorStyle}
                  multiple
                  id="combo-box-demo"
                  options={emailList?.map((option) => option)}
                  onChange={(
                    e: EventTarget,
                    value: [],
                    situation: string,
                    // option,
                  ) => {
                    setSelectedEmailList(value);
                  }}
                  renderTags={(
                    value: [],
                    getTagProps: (arg0: {
                      index: number;
                    }) => JSX.IntrinsicAttributes,
                  ) => {
                    // setSelectedEmailList(value);
                    return value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                      />
                    ));
                  }}
                  renderInput={(params: JSX.IntrinsicAttributes) => (
                    <TextField
                      {...params}
                      label={strings.displayText.emailAddress}
                      name="email"
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        handleInputChange('email', e);
                        handleEmailChange(e);
                      }}
                      // placeholder="user email"
                      helperText={formError?.email}
                    />
                  )}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                margin="normal"
                required
                fullWidth
                multiline={true}
                rows={4}
                label={strings.displayText.message}
                name="description"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  handleInputChange('description', e);
                  debounceHandler('description', e);
                }}
                sx={[customStyles.textErrorStyle, { mb: 2 }]}
                helperText={formError?.description}
              />
            </Grid>

            {/* <Grid item xs={6} my={2}>
              <input
                type="file"
                onChange={handleFileInputChange}
                accept="image/*"
              />
            </Grid> */}
          </Grid>
          <Buttons
            style={{ py: '0.8rem', mt: '1rem' }}
            type="submit"
            fullWidth={true}
          >
            Send
          </Buttons>
        </Box>
      </Box>
    </Fragment>
  );
};

export default GroupChat;
